import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Row,
  Col,
  Alert,
  Spinner,
  FormGroup,
  Input,
} from "reactstrap";
import logo from "../../assets/img/icon-transparent.png";
import {
  validateQuotationUUID,
  updateQuotationStatus,
} from "actions/quotationReview";
import { useNavigate, useParams } from "react-router-dom";
import { showAlert } from "components/Common/commonFunctions";
import NotificationAlert from "react-notification-alert";
import { WORK_ORDER } from "components/Common/constants";

const QuotationReview = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [quotationStatus, setQuotationStatus] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [resetStatus, setResetStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotationData, setQuotationData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const notificationAlertRef = useRef(null);

  const notify = (type, message) => {
    let finalMessage = "Something went wrong";

    if (typeof message === "string") {
      finalMessage = message;
    } else if (Array.isArray(message)) {
      finalMessage = message.map((err) => err?.Msg).join(", ");
    } else if (typeof message === "object" && message !== null) {
      finalMessage = message.Msg || JSON.stringify(message);
    }

    const options = {
      place: "tr",
      message: <div>{finalMessage}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };

    notificationAlertRef.current.notificationAlert(options);
  };

  // Get UUID for validation
  useEffect(() => {
    getValidationUUID(uuid);
  }, [uuid]);

  // Validate and fetch quotation data
  const getValidationUUID = async () => {
    try {
      setLoading(true);
      const res = await validateQuotationUUID(uuid);
      if (res?.code === 200 && res?.data?.isValid == true) {
        setResetStatus(null);
        setQuotationData(res.data.quotationResponse);
        // Check if quotation is already accepted/rejected
        if (
          res.data.quotationResponse.quotationStatus === "QUOTATION_APPROVED"
        ) {
          setQuotationStatus("approved");
        } else if (
          res.data.quotationResponse.quotationStatus === "QUOTATION_REJECTED"
        ) {
          setQuotationStatus("rejected");
        } else {
        }
      } else {
        setResetStatus("error");
      }
    } catch (err) {
      setResetStatus("error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return amount?.toFixed(2) || "0.00";
  };

  // Show loading state
  if (loading) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }}>
            Loading...
          </Spinner>
          <p className="mt-3 text-muted">Loading details...</p>
        </div>
      </Container>
    );
  }

  // Show error if UUID is invalid
  if (resetStatus === "error") {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <Card
          className="shadow-lg border-0"
          style={{ maxWidth: "600px", width: "100%", minHeight: "400px" }}
        >
          <CardBody className="d-flex flex-column align-items-center justify-content-center text-center p-5">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mb-4"
              style={{
                width: "80px",
                height: "80px",
                backgroundColor: "#fff3cd",
                margin: "0 auto",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#856404"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <Alert
              color="warning"
              className="w-100 mb-0"
              style={{ backgroundColor: "#fff3cd", borderColor: "#ffeeba" }}
            >
              <h4 className="alert-heading" style={{ color: "#856404" }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#856404"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    display: "inline-block",
                    marginRight: "10px",
                    verticalAlign: "middle",
                  }}
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                Invalid Link
              </h4>
              <p
                className="mb-0"
                style={{ color: "#856404", fontSize: "1.1rem" }}
              >
                This quotation link is invalid or has expired.
              </p>
            </Alert>

            {/* <Button
              color="warning"
              className="mt-4 px-4"
              onClick={() => (window.location.href = "/")}
              style={{
                backgroundColor: "#ffc107",
                borderColor: "#ffc107",
                color: "#856404",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  marginRight: "8px",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-5v-8H7v8H5a2 2 0 0 1-2-2z" />
              </svg>
              Go to Homepage
            </Button> */}
          </CardBody>
        </Card>
      </Container>
    );
  }

  // If no data, show error
  if (!quotationData) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <Alert color="warning" className="text-center">
          <h4 className="alert-heading">No Data Found</h4>
          <p>Unable to load quotation details.</p>
        </Alert>
      </Container>
    );
  }

  // Prepare items for table display
  const allItems = [
    ...(quotationData.quotationItems?.spareParts || []),
    ...(quotationData.quotationItems?.services || []),
  ];

  const subTotal = quotationData.subtotal || 0;
  const sparePartTotal = quotationData.sparePartTotal || 0;
  const serviceTotal = quotationData.serviceTotal || 0;
  const discountedTotal = quotationData.discountedTotal || 0;
  const vatAmount = quotationData.vatAmount || 0;
  const grandTotal = quotationData.grandTotal || 0;
  const vatRate = quotationData.vat || 0;

  const handleRejectQuotation = async () => {
    try {
      let body = {
        action: "REJECT",
      };
      const res = await updateQuotationStatus(uuid, body);
      if (res?.code === 200) {
        notify("success", res.message || "Quotation Rejected Successfully..");
        getValidationUUID();
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to update quotation status";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to update quotation status");
    }
  };

  const handleApproveQuotation = async () => {
    try {
      let body = {
        action: "APPROVE",
      };
      const res = await updateQuotationStatus(uuid, body);
      if (res?.code === 200) {
        if (res?.data?.quotationStatus !== "QUOTATION_ACCEPTED") {
          notify("success", res.message || "Quotation Approved Successfully..");
          getValidationUUID();
        } else {
          notify("success", "Quotation Accepted Successfully..");
          setTimeout(() => {
            navigate(`/payment/confirm`, {
              state: { quotationData: quotationData, uuid: uuid },
            });
          }, 2000);
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to update quotation status";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to update quotation status");
    }
  };

  const handleConfirmationAlerts = (type) => {
    switch (type) {
      case "approve":
        return showAlert({
          title: "Are you sure you want to accept this quotation?",
          type: "warning",
          message: "This action cannot be undone. Please confirm to continue.",
          confirmStyle: "default",
          cancelStyle: "secondary",
          confirmText: "Yes, Accept",
          cancelText: "No",
          onConfirm: () => handleApproveQuotation(),
          onCancel: () => {},
        });
      case "reject":
        return showAlert({
          title: "Are you sure you want to reject this quotation?",
          type: "warning",
          message:
            "This action cannot be undone. Please provide a reason for rejection.",
          confirmStyle: "danger",
          cancelStyle: "secondary",
          confirmText: "Yes, Reject",
          cancelText: "No",
          // onConfirm: () => {},
          onConfirm: () => handleRejectQuotation(),
          onCancel: () => {},
          // children: (
          //   <FormGroup className="mt-2 d-flex flex-column align-items-start">
          //     <Input
          //       name="rejectReason"
          //       type="textarea"
          //       placeholder="Enter reason *"
          //       // value={inputs.rejectReason}
          //       // onChange={(e) => {
          //       //   e.stopPropagation();
          //       //   handleCommonInputs("rejectReason", e.target.value);
          //       // }}
          //       onChange={(e) => {
          //       }}
          //     />
          //   </FormGroup>
          // ),
        });

      default:
        break;
    }
  };

  const navigateToConfirmPage = () => {
    setLoading(true);
    setTimeout(() => {
      navigate(`/payment/confirm`, {
        state: { quotationData: quotationData, uuid: uuid },
      });
      setLoading(false);
    }, 2000);
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <Container fluid className="quotation-container-review bg-light py-5">
        <Col lg={10} xl={9}>
          <Card className="shadow-sm border-0 rounded-3">
            <CardBody className="p-0">
              {/* Header with Logo */}
              <div className="quotation-header border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="logo-section">
                  <img src={logo} alt="Company Logo" className="company-logo" />
                </div>
                <div
                  className="justify-content-center text-right"
                  style={{ marginRight: "40px" }}
                >
                  <h1 className="h3 mb-0 fw-bold" style={{ color: "#0ea5e9" }}>
                    {WORK_ORDER} No : {quotationData?.jobID || "N/A"}
                  </h1>
                  <h5 style={{ color: "#6d7275ff" }}>
                    Quotation No : {quotationData?.ID || "N/A"}
                  </h5>
                  {quotationStatus && (
                    <Badge
                      color={
                        quotationStatus === "approved" ? "success" : "danger"
                      }
                      pill
                    >
                      {quotationStatus === "approved" ? "APPROVED" : "REJECTED"}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Items Table */}
              <div className="table-responsive p-4 pt-3">
                <Table bordered hover responsive className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center" style={{ minWidth: "120px" }}>
                        ITEM NUMBER
                      </th>
                      <th className="text-center" style={{ minWidth: "300px" }}>
                        DESCRIPTION
                      </th>
                      <th className="text-center" style={{ minWidth: "80px" }}>
                        QTY
                      </th>
                      <th className="text-center" style={{ minWidth: "100px" }}>
                        UNIT PRICE (SAR)
                      </th>
                      <th className="text-center" style={{ minWidth: "100px" }}>
                        DISCOUNT (SAR)
                      </th>
                      <th className="text-center" style={{ minWidth: "110px" }}>
                        TOTAL (SAR)
                      </th>
                      <th className="text-center" style={{ minWidth: "130px" }}>
                        AFTER DISCOUNT (SAR)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Spare Parts Section */}
                    {quotationData.quotationItems?.spareParts?.length > 0 && (
                      <>
                        <tr className="table-secondary">
                          <td colSpan="7">
                            <strong>📦 Spare Parts</strong>
                          </td>
                        </tr>
                        {quotationData.quotationItems.spareParts.map(
                          (item, index) => (
                            <tr key={item.ID || `spare-${index}`}>
                              <td
                                data-label="ITEM NUMBER"
                                className="text-center"
                              >
                                {item.itemNumber ||
                                  item.itemID ||
                                  `Item ${index + 1}`}
                              </td>
                              <td
                                data-label="DESCRIPTION"
                                className="text-center"
                              >
                                {item.description || "No description"}
                              </td>
                              <td
                                data-label="QTY"
                                className="quotation-table-data-end"
                              >
                                {item.quantity || 1}
                              </td>
                              <td
                                data-label="UNIT PRICE (SAR)"
                                className="quotation-table-data-end"
                              >
                                {formatCurrency(item.unitPrice)}
                              </td>
                              <td
                                data-label="DISCOUNT"
                                className="quotation-table-data-end"
                              >
                                {item.discount ? (
                                  <span className="text-warning">
                                    {item.discount}%
                                    {item.discountAmount > 0 &&
                                      ` (${formatCurrency(item.discountAmount)} SAR)`}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td
                                data-label="TOTAL (SAR)"
                                className="quotation-table-data-end"
                              >
                                {formatCurrency(item.totalPrice)}
                              </td>
                              <td
                                data-label="AFTER DISCOUNT (SAR)"
                                className="quotation-table-data-end"
                              >
                                {item.discount > 0 ? (
                                  <span className="fw-semibold text-success">
                                    {formatCurrency(
                                      item.discountedTotalPrice ||
                                        item.totalPrice,
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-muted">
                                    {formatCurrency(item.totalPrice)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ),
                        )}
                      </>
                    )}

                    {/* Services Section */}
                    {quotationData.quotationItems?.services?.length > 0 && (
                      <>
                        <tr className="table-secondary">
                          <td colSpan="7">
                            <strong>🔧 Services</strong>
                          </td>
                        </tr>
                        {quotationData.quotationItems.services.map(
                          (item, index) => (
                            <tr key={item.ID || `service-${index}`}>
                              <td
                                data-label="ITEM NUMBER"
                                className="text-center"
                              >
                                {item.itemNumber || "-"}
                              </td>
                              <td
                                data-label="DESCRIPTION"
                                className="text-center"
                              >
                                {item.description ||
                                  item.title ||
                                  "No description"}
                              </td>
                              <td
                                data-label="QTY"
                                className="quotation-table-data-end"
                              >
                                {item.quantity || 1}
                              </td>
                              <td
                                data-label="UNIT PRICE (SAR)"
                                className="quotation-table-data-end"
                              >
                                {formatCurrency(item.unitPrice)}
                              </td>
                              <td
                                data-label="DISCOUNT"
                                className="quotation-table-data-end"
                              >
                                {item.discount > 0 ? (
                                  <span className="text-warning">
                                    {item.discount}%
                                    {item.discountAmount > 0 &&
                                      ` (${formatCurrency(item.discountAmount)} SAR)`}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td
                                data-label="TOTAL (SAR)"
                                className="quotation-table-data-end"
                              >
                                {formatCurrency(item.totalPrice)}
                              </td>
                              <td
                                data-label="AFTER DISCOUNT (SAR)"
                                className="quotation-table-data-end"
                              >
                                {item.discount > 0 ? (
                                  <span className="fw-semibold text-success">
                                    {formatCurrency(
                                      item.discountedTotalPrice ||
                                        item.totalPrice,
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-muted">
                                    {formatCurrency(item.totalPrice)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ),
                        )}
                      </>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Summary Section */}
              <div className="px-4 pb-4" style={{ marginRight: "10px" }}>
                <Row className="justify-content-end">
                  <Col md={6} lg={5}>
                    <Card className="shadow-sm-light border-0">
                      <CardBody>
                        {quotationData.sparePartTotal > 0 && (
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">
                              Spare Parts Total:
                            </span>
                            <span>
                              {formatCurrency(
                                quotationData.sparePartTotal || 0,
                              )}
                            </span>
                          </div>
                        )}

                        {quotationData.serviceTotal > 0 && (
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Services Total:</span>
                            <span>
                              {formatCurrency(quotationData.serviceTotal || 0)}
                            </span>
                          </div>
                        )}
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Subtotal:</span>
                          <strong>
                            {formatCurrency(quotationData.subtotal || 0)}
                          </strong>
                        </div>
                        {quotationData.discount > 0 && (
                          <>
                            <div className="d-flex justify-content-between mb-2 text-warning">
                              <span>
                                Discount ({quotationData.discountPercentage}%):
                              </span>
                              <span>
                                -{" "}
                                {formatCurrency(
                                  (quotationData.subtotal || 0) -
                                    (quotationData.discountedTotal || 0),
                                )}
                              </span>
                            </div>

                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">
                                Discounted Total :
                              </span>
                              <strong>
                                {formatCurrency(
                                  quotationData.discountedTotal || 0,
                                )}
                              </strong>
                            </div>
                          </>
                        )}

                        <hr className="my-2" />

                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            VAT ({quotationData.vat || 0}%):
                          </span>
                          <span>
                            {formatCurrency(quotationData.vatAmount || 0)}
                          </span>
                        </div>

                        <hr className="my-2" />

                        <div className="d-flex justify-content-between mt-2 pt-2">
                          <span className="fw-bold fs-5">Grand Total:</span>
                          <span className="fw-bold fs-5">
                            {formatCurrency(quotationData.grandTotal || 0)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mt-2 pt-2">
                          <span className="fw-bold fs-5">Paid Amount:</span>
                          <span className="fw-bold fs-5">
                            {formatCurrency(quotationData.paidAmount || 0)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mt-2 pt-2">
                          <span className="fw-bold fs-5">Payable Amount:</span>
                          <span className="fw-bold fs-5 text-success">
                            {formatCurrency(quotationData.payableAmount || 0)}
                          </span>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>

              {/* Action Buttons - Show only if not already approved/rejected */}
              {!quotationStatus && (
                <div className="action-buttons p-4 d-flex gap-3 justify-content-end border-top">
                  <Button
                    color="danger"
                    onClick={() => handleConfirmationAlerts("reject")}
                    className="px-5 py-2 fw-semibold"
                    disabled={loading}
                    style={{ borderRadius: "8px" }}
                  >
                    ✗ Reject Quotation
                  </Button>
                  <Button
                    color="success"
                    onClick={() => {
                      if (
                        quotationData?.quotationStatus === "QUOTATION_ACCEPTED"
                      ) {
                        navigateToConfirmPage();
                      } else {
                        handleConfirmationAlerts("approve");
                      }
                    }}
                    className="px-5 py-2 fw-semibold"
                    disabled={loading}
                    style={{
                      borderRadius: "8px",
                      backgroundColor: "#10b981",
                      borderColor: "#10b981",
                      alignItems: "center",
                    }}
                  >
                    {quotationData?.quotationStatus === "QUOTATION_ACCEPTED"
                      ? "✓ Make Payment"
                      : "✓ Approve Quotation"}
                  </Button>
                </div>
              )}

              {/* Show message after action */}
              {quotationStatus && (
                <Alert
                  color={quotationStatus === "approved" ? "success" : "danger"}
                  className="mx-4 mb-4"
                >
                  {quotationStatus === "approved"
                    ? "✓ Quotation has been approved successfully"
                    : "✗ Quotation has been rejected."}
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      </Container>
    </>
  );
};

export default QuotationReview;
